import axios from 'axios';
import { API } from '../contexts/AuthContext';

export interface Product {
  id: string;
  name: string;
  price: number;
  mrp: number | null;
  currency: string;
  url: string;
  imageUrls: string[];
  category: string | null;
  // Present on Typesense-backed results only (GET /api/products/scene).
  productType?: string | null;
  brand?: string | null;
  source?: string | null;
  rating?: number | null;
}

/** The markets the catalog covers. Prices are native to each — never converted. */
export type Market = 'IN' | 'US';

/**
 * The catalog's only real brand distinction — three retailers, i.e. the
 * `source` field server-side. Not the product-level `brand` field (a Wayfair
 * designer sub-brand for hundreds of values) — the panel filters by retailer.
 */
export type Brand = 'wakefit' | 'ikea' | 'wayfair';

/**
 * Label of the catch-all group the server returns when the scene yields no
 * products — mirrors BROWSE_LABEL in backend/src/lib/productSearch.ts. Only the
 * panel's wording depends on it; a drift would cost a phrase, never content.
 */
export const BROWSE_LABEL = 'all products';

/**
 * Every category the catalog carries, for the panel's "browse a category"
 * dropdown — mirrors CATALOG_CATEGORIES in backend/src/lib/productSearch.ts.
 * Deliberately not the same list as sceneShop.ts's detection allowlist: this
 * one exists so a user can shop a category the room photo never detected at
 * all, so it has to be the catalog's full set, not just the detectable subset.
 */
export const CATALOG_CATEGORIES = [
  'Appliances', 'Baby & Kids', 'Bath', 'Decor', 'Electronics', 'Furniture',
  'Home Improvement', 'Kitchen', 'Lighting', 'Office', 'Other', 'Outdoor',
  'Pet', 'Rugs & Textiles', 'Seasonal', 'Storage',
] as const;

/** One detected room object and the products matching it. */
export interface SceneGroup {
  label: string;
  categories: string[];
  total: number;
  products: Product[];
}

export interface SceneResult {
  /** The market the results were scoped to — geo-detected unless one was asked for. */
  country: Market;
  groups: SceneGroup[];
}

export async function getProducts(category: string, limit?: number): Promise<Product[]> {
  const { data } = await axios.get<{ products: Product[] }>(`${API}/products`, {
    params: { category, ...(limit !== undefined ? { limit } : {}) },
  });
  return data.products;
}

/**
 * Products for the objects detected in a room photo. The backend drops labels
 * it has no category rule for, so the returned groups are a subset of `labels`
 * — except when none survive (including an empty `labels`), where it answers
 * with a single unguarded "all products" browse group.
 * `q` narrows within the scene — it is appended to each label's query server-side.
 *
 * The market is the server's call — it geo-locates the caller (CF-IPCountry, else
 * the client IP) and says which one it used in `country`. We deliberately never
 * send a country: the same geo decides billing currency, so letting the shopping
 * UI pin a market would put the two out of step with each other.
 *
 * `brand` narrows to one or more retailers (see `Brand`) and, like `q`, applies
 * across every group in the response — not just the active tab.
 *
 * `category` (one of `CATALOG_CATEGORIES`) adds one extra group scoped to that
 * category alone, regardless of whether it was detected in the photo — this is
 * what the panel's "browse a category" dropdown sends.
 */
export async function getSceneProducts(
  labels: string[],
  q?: string,
  perLabel?: number,
  brand?: Brand[],
  category?: string,
): Promise<SceneResult> {
  const { data } = await axios.get<SceneResult>(`${API}/products/scene`, {
    params: {
      ...(labels.length ? { labels: labels.join(',') } : {}),
      ...(q ? { q } : {}),
      ...(perLabel !== undefined ? { per_label: perLabel } : {}),
      ...(brand?.length ? { brand } : {}),
      ...(category ? { category } : {}),
    },
  });
  return data;
}
